import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useInView, animate } from "framer-motion";
import { PROJECTS as STATIC_PROJECTS } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgSrc = (u) => (!u ? "" : u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const Counter = ({ to, suffix, testid }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, to, {
            duration: 2,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setVal(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to]);

    return (
        <span ref={ref} className="font-heading font-extrabold tracking-tighter text-5xl md:text-7xl text-navy" data-testid={testid}>
            {val}{suffix}
        </span>
    );
};

export const Portfolio = () => {
    const [dbProjects, setDbProjects] = useState([]);

    useEffect(() => {
        axios.get(`${API}/projects`).then((r) => setDbProjects(r.data)).catch(() => {});
    }, []);

    const items = dbProjects.length > 0
        ? dbProjects.map((p) => ({ image: imgSrc(p.images?.[0] || ""), title: p.title, meta: p.client || "", desc: p.description || "" }))
        : STATIC_PROJECTS.map((p) => ({ ...p, desc: "" }));

    return (
    <section id="projects" data-testid="projects-section" className="bg-bone py-24 lg:py-32 border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mb-16 lg:mb-20"
            >
                <p className="font-mono text-[11px] tracking-[0.35em] text-gold mb-6" data-testid="projects-overline">
                    04 / DELIVERED FLOORS
                </p>
                <h2 className="font-heading font-extrabold tracking-tighter text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="projects-heading">
                    PROOF, NOT PROMISES.
                </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-20" data-testid="projects-grid">
                {items.map((p, i) => (
                    <motion.div
                        key={p.title + i}
                        initial={{ clipPath: "inset(100% 0 0 0)" }}
                        whileInView={{ clipPath: "inset(0% 0 0 0)" }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
                        className="group relative h-[300px] md:h-[400px] overflow-hidden bg-navy"
                        data-testid={`project-card-${i}`}
                    >
                        {p.image && (
                            <img
                                src={p.image}
                                alt={p.title}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                            />
                        )}
                        <div className="absolute inset-0 bg-navy/30 group-hover:bg-navy/15 transition-colors duration-500" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 bg-navy/85 backdrop-blur-sm">
                            <h3 className="font-heading font-bold tracking-tight text-white text-lg md:text-xl">{p.title}</h3>
                            {p.meta && <p className="font-mono text-[10px] tracking-[0.25em] text-gold mt-1 uppercase">{p.meta}</p>}
                            {p.desc && <p className="text-sm text-slate-300 mt-2 leading-relaxed">{p.desc}</p>}
                        </div>
                    </motion.div>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-line" data-testid="stats-counters">
                <div className="border-b border-r border-line p-10 bg-white text-center md:text-left">
                    <Counter to={1000} suffix="+" testid="stat-projects" />
                    <p className="mt-3 font-mono text-[11px] tracking-[0.25em] text-muted">PROJECTS DELIVERED</p>
                </div>
                <div className="border-b border-r border-line p-10 bg-white text-center md:text-left">
                    <Counter to={100} suffix="%" testid="stat-factory-direct" />
                    <p className="mt-3 font-mono text-[11px] tracking-[0.25em] text-muted">FACTORY DIRECT</p>
                </div>
                <div className="border-b border-r border-line p-10 bg-navy text-center md:text-left">
                    <span className="font-heading font-extrabold tracking-tighter text-5xl md:text-7xl text-gold" data-testid="stat-quality">
                        TASKLANCE®
                    </span>
                    <p className="mt-3 font-mono text-[11px] tracking-[0.25em] text-slate-400">PREMIUM QUALITY STANDARD</p>
                </div>
            </div>
        </div>
    </section>
    );
};
